import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { useAgentTeller } from '../agent-teller.js';
import { useContentMap } from '../content.js';
import { SPECIAL_CATEGORY_DATA } from '../email-templates/templates.js';
import { downloadTemplateEml } from '../email-templates/eml.js';
import {
  fetchCallEnrichment,
  type CallEnrichment,
} from '../genesys/call-enrichment.js';
import {
  groupCallsByCorrelation,
  eventsForGroup,
  type CallRecord,
  type CallEvent,
  type GroupedCall,
} from './call-history-grouping.js';

/**
 * SCRN 13 — Call History (CCF-104). A record of the CURRENT customer's
 * interaction journey: who handled the call, when, the call type/direction, the
 * Genesys-native wrap code + queue and the AI call summary. Read-only.
 *
 * Per #165 / CCF-104, the screen looks up the call history for the customer the
 * agent is currently working on — there is NO customer-CIF search box and NO
 * Teller-ID input on this screen. (The BFF route remains Teller-ID gated: the
 * mandatory per-call teller gate is preserved server-side and the agent's
 * profile/context Teller ID is forwarded in the request header.) The records
 * load automatically once a customer is in context.
 *
 * The top "Call history" table shows ONE ROW PER CALL — the customer's
 * interactions grouped by correlation id (#213), NOT one row per underlying API
 * record. A single session (one correlation id) collapses into a single row even
 * when it produced several journey records. Newest-first, paginated 10 per page
 * (#165) with Previous / Next controls, each row carrying:
 *   - GUID ID (the conversationId for an Online call, or the per-session
 *     Web-App v4 GUID for an Offline call — #165 — which is the correlation id
 *     the row is grouped by, #213)
 *   - Agent identifier (email / UPN)
 *   - Call date/time (the interaction start)
 *   - Call Type (Online / Offline; "Online Inbound" / "Online Outbound" when the
 *     direction is known for an Online call)
 *   - Wrap Code (Online calls only — from Genesys)
 *   - Queue (Online calls only — from Genesys)
 *   - Call Notes (the AI call summary — Online calls only; retrieved only when
 *     the agent opens the call, i.e. selects a row)
 *
 * When the agent selects a call, a Call details table lists that call's events
 * (CCF-112 "Event Details"): Event, Event Outcome, Date/time and the agent
 * identifier.
 *
 * Data fidelity note (CCF-104): the Genesys-native fields — Wrap Code, Queue
 * and the AI Call summary — are Genesys-sourced and are retrieved LAZILY when
 * the agent OPENS a call (selects the row), via the BFF `/calls/enrichment`
 * proxy (which performs the Genesys Analytics + routing + summary reads
 * server-side with the agent's forwarded Genesys token). This matches the
 * CCF-104 spec — the AI summary is "retrieved … only when the agent clicks on
 * the call" — and Queue + Wrap Code come from the same enrichment call. Each
 * field is best-effort: an Offline call, an unavailable Genesys token (e.g. the
 * headless mock path / a non-embedded surface) or an unsummarised call degrades
 * the field to a graceful "—" placeholder. The enrichment merges over any
 * value the journey record already carried.
 */

// The rendered `CallRecord` / `CallEvent` shapes and the grouped-call transform
// live in ./call-history-grouping (a pure, unit-tested module — #213).

/** Newest-first page size for the top "Call history" table (#165): show the
 *  most recent 10, then page through the rest with Next / Previous. */
const PAGE_SIZE = 10;

/**
 * Genesys interaction "admin" page for reviewing a call (recording, transcript,
 * timeline). For an Online call the correlation id IS the Genesys
 * conversationId, so the GUID ID cell links to this page in a new tab (#252 —
 * Dylan). Offline calls carry a Web-App GUID (no Genesys interaction), so their
 * id stays plain text.
 */
const GENESYS_INTERACTION_BASE =
  'https://apps.mypurecloud.ie/directory/#/analytics/interactions';

/** The Genesys interaction-review URL for an Online call's conversationId. */
function genesysInteractionUrl(conversationId: string): string {
  return `${GENESYS_INTERACTION_BASE}/${encodeURIComponent(conversationId)}/admin`;
}

/** Formats an ISO-8601 timestamp as a readable local date-time. */
function formatDateTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Renders the Call Type cell (CCF-104): "Online Inbound" / "Online Outbound"
 * when the call is Online and the direction is known, "Online" / "Offline"
 * otherwise. "—" when the record carries no call type.
 */
function callTypeLabel(c: GroupedCall): string {
  if (!c.callType) return '—';
  if (c.callType === 'Online' && c.direction) {
    return `Online ${c.direction}`;
  }
  return c.callType;
}

/** The GUID ID shown in the table (#165) — the conversationId (Online) or the
 *  per-session Web-App GUID (Offline). This IS the correlation id the row is
 *  grouped by (#213). */
function correlationFor(c: GroupedCall): string {
  return c.correlationId || '—';
}

/**
 * Coerces a Genesys-native enrichment field (Wrap Code, Queue, AI Call summary)
 * to a plain string for rendering, or `undefined` when it carries no value.
 *
 * #556: the enrichment arrives from the BFF as an unvalidated cast, and a
 * Genesys AI summary can be a STRUCTURED OBJECT in some records (string in
 * others). Rendering that object straight as a React child crashed the whole
 * Call History page ("Objects are not valid as a React child") for any call
 * that HAD an AI summary — while calls without one rendered fine. The BFF
 * adapter now flattens the summary to a string, and this is the belt-and-braces
 * guard so a non-string value can never crash the render again (mirrors the
 * #262 / #283 "coerce the gateway scalar before use" fixes).
 */
function displayText(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined;
  if (value && typeof value === 'object') {
    const text = (value as { text?: unknown }).text;
    if (typeof text === 'string') return text.trim() || undefined;
    return undefined;
  }
  if (typeof value === 'number') return String(value);
  return undefined;
}

export function CallHistoryScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate);

  // Teller ID is NOT entered on this screen (#165). It is still mandatory on the
  // BFF call (the per-call teller gate is preserved server-side), so we forward
  // the agent's profile Teller ID, falling back to the in-context value.
  const agentTellerId = useAgentTeller();
  // #637 inc 26 — admin-editable Special Category Data email wording.
  const contentMap = useContentMap();
  const cif = customer.cif ?? '';
  const tellerId = /^\d{4}$/.test(agentTellerId ?? '')
    ? agentTellerId!
    : (customer.tellerId ?? '');

  // The RAW journey records from the BFF; grouped into one row per call
  // (by correlation id, #213) for display below.
  const [calls, setCalls] = useState<CallRecord[]>([]);
  // 0-based page index into the GROUPED calls (10 rows per page, newest — #165).
  const [page, setPage] = useState(0);
  const [events, setEvents] = useState<CallEvent[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // The selected call's correlation id (the grouped-row key, #213).
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Per-call Genesys enrichment (Queue, Wrap Code, AI summary), fetched lazily
  // when the agent opens a call and cached by CORRELATION ID so re-selecting a
  // call does not re-fetch. Keyed by the grouped-call key so the whole session
  // is enriched once (#213 / CCF-104).
  const [enrichment, setEnrichment] = useState<Record<string, CallEnrichment>>(
    {},
  );

  /**
   * Opens a call: reveals its Call details and lazily fetches the Genesys-native
   * fields (Queue, Wrap Code, AI Call summary) for an Online call — "retrieved
   * … only when the agent clicks on the call" (CCF-104). Offline calls carry no
   * Genesys data, and a call already enriched is not re-fetched. Bound to the
   * call's correlation id (the grouped-row key, #213).
   */
  function selectCall(c: GroupedCall): void {
    setSelectedId(c.correlationId);
    if (c.callType !== 'Online') return;
    if (enrichment[c.correlationId]) return;
    if (!c.correlationId) return;
    void (async () => {
      const data = await fetchCallEnrichment(
        cif,
        c.correlationId,
        tellerId,
        'Online',
        // The agent's CURRENT live interaction id (distinct from
        // `c.correlationId`, which is the — possibly past — call being
        // reviewed): needed server-side to resolve this agent's own
        // Online/Offline RBAC state (ai-review MEDIUM, #270).
        customer.contextConversationId,
      );
      setEnrichment((prev) => ({ ...prev, [c.correlationId]: data }));
    })();
  }

  /**
   * Loads the per-event "Event Details" group (CCF-112) for the current
   * customer via the generated client. Best-effort relative to the journey
   * load: a failure here leaves the events empty but never blocks the table.
   */
  async function loadEvents(custCif: string, teller: string): Promise<void> {
    try {
      const { data, error: apiError } = await api.POST('/calls/events', {
        params: { header: { 'X-Teller-Id': teller } },
        body: { customerNumber: custCif },
      });
      if (apiError || !data) {
        setEvents([]);
        return;
      }
      setEvents((data.events ?? []) as unknown as CallEvent[]);
    } catch {
      setEvents([]);
    }
  }

  /** Loads the history for the current customer. */
  async function loadFor(custCif: string, teller: string): Promise<void> {
    setLoading(true);
    setError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/calls/history', {
        params: { header: { 'X-Teller-Id': teller } },
        body: { customerNumber: custCif },
      });
      if (apiError || !data) {
        setError(
          response.status === 403
            ? 'Forbidden: check your permissions.'
            : `Could not load call history (${response.status}).`,
        );
        return;
      }
      // Keep the FULL newest-first list (#165) — pagination windows it 10 at a
      // time client-side; the BFF returns the customer's interactions in order.
      const rows = (data.calls ?? []) as unknown as CallRecord[];
      setCalls(rows);
      // Reset to the first page on a fresh customer load (#165).
      setPage(0);
      setLoaded(true);
      setSelectedId(null);
      await loadEvents(custCif, teller);
    } catch {
      setError('Request failed.');
    } finally {
      setLoading(false);
    }
  }

  // Auto-load the current customer's call history. No search box / Teller-ID
  // entry (#165): the screen shows the history for the customer the agent is
  // currently working on, loaded the moment that customer + a valid Teller ID
  // are in context. Keyed on the context so it re-runs once it populates.
  useEffect(() => {
    if (!cif) return;
    if (!/^\d{4}$/.test(tellerId)) return;
    void loadFor(cif, tellerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cif, tellerId]);

  // ONE ROW PER CALL (#213): collapse the raw journey records into grouped calls
  // by correlation id (Online = conversationId, Offline = per-session GUID). The
  // top table paginates over these, not the raw records.
  const groupedCalls = groupCallsByCorrelation(calls);

  // Pagination window (#165): 10 rows per page, newest-first. The page index is
  // clamped so a stale page (e.g. after a smaller customer loads) never points
  // past the end.
  const pageCount = Math.max(1, Math.ceil(groupedCalls.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageStart = currentPage * PAGE_SIZE;
  const pageRows = groupedCalls.slice(pageStart, pageStart + PAGE_SIZE);
  const showingFrom = groupedCalls.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + PAGE_SIZE, groupedCalls.length);

  const selectedCall = groupedCalls.find((c) => c.correlationId === selectedId);
  // CCF-112 "Event Details" for the selected call: the events belonging to that
  // grouped call — matched by correlation id (or a member interaction id for
  // events recorded before the correlation-id stamp), newest-first (#213).
  const selectedEvents = selectedCall
    ? eventsForGroup(selectedCall, events)
    : [];

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="call-history-heading"
    >
      {/* Top row: back link + Special category data (faithful port). */}
      <div className="flex items-center justify-between mb-6">
        <button
          type="button"
          onClick={() => window.history.back()}
          aria-label="Back to customer dashboard"
          className="flex items-center gap-2 text-[16px] font-['Lato','Arial',sans-serif] text-[#D23A06] underline decoration-[#D23A06] hover:no-underline focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded px-2 py-1 -ml-2 transition-all"
        >
          <span aria-hidden="true">&lt;</span>
          <span>Back to customer dashboard</span>
        </button>

        {/* Special Category Data (#254 / Drive "SCRN 13"): downloads the
            operator's "Special Category Data" template as a prefilled, formatted
            Outlook draft (`.eml`, #434 Option A — the soft-introduction consent
            script + the fields to record) so the agent sends it to their Team
            Leader from Outlook with the source look-and-feel preserved. Prefilled
            best-effort from the in-context customer (name + CIF); recipient and
            the remaining lines are left blank for the agent. */}
        <button
          type="button"
          data-template-id={SPECIAL_CATEGORY_DATA.id}
          onClick={() =>
            downloadTemplateEml(
              SPECIAL_CATEGORY_DATA,
              {
                customerName: customer.name ?? '',
                cif: customer.cif ?? '',
                content: contentMap,
              },
              undefined,
              tellerId,
            )
          }
          aria-label="Special Category Data"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[44px] bg-[#FF703E] text-[#1D252C] font-['Lato','Arial',sans-serif] text-[16px] font-bold rounded-[4px] shadow-sm hover:bg-[#e55a2a] hover:shadow-md focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#005fcc] transition-all"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m2 7 10 6 10-6" />
          </svg>
          <span>Special category data</span>
        </button>
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="call-history-heading"
          className="text-[24px] font-['Lato','Arial',sans-serif] font-bold text-[#1D252C] mb-6"
        >
          Customer call history
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-6 p-4 bg-white border border-[#dc2626] font-['Lato','Arial',sans-serif] text-[14px] text-[#1d252c] max-w-[1200px]"
        >
          {error}
        </div>
      )}

      {/* No customer in context: nothing to show (#165 — the screen looks up the
          customer the agent is currently working on, not a searched CIF). */}
      {!cif && !loading && (
        <p
          className="mb-6 font-['Lato','Arial',sans-serif] text-[16px] text-[#1d252c]"
          data-testid="no-customer"
        >
          Identify a customer to view their call history.
        </p>
      )}

      {loading && !loaded && (
        <p className="mb-6 font-['Lato','Arial',sans-serif] text-[16px] text-[#6b7280]">
          Loading call history…
        </p>
      )}

      {/* Top "Call history" table — most recent 10 calls, selectable. */}
      {loaded && (
        <div className="mb-6">
          <div className="border-2 border-[#dad7d3] bg-white max-w-[1400px] overflow-x-auto">
            <table
              className="w-full border-collapse"
              role="table"
              aria-label="Customer call history"
            >
              <thead>
                <tr className="bg-[#f3f4f6]">
                  {[
                    'GUID ID',
                    'Agent',
                    'Call date/time',
                    'Call type',
                    'Wrap code',
                    'Queue',
                    'Call notes',
                  ].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      className="text-left px-4 py-3 text-[14px] font-['Lato','Arial',sans-serif] font-bold text-[#1d252c] border-b-2 border-r border-[#dad7d3] last:border-r-0"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.length > 0 ? (
                  pageRows.map((c) => {
                    const isSelected = selectedId === c.correlationId;
                    const isOnline = c.callType === 'Online';
                    // Lazily-fetched Genesys enrichment merged over the call's
                    // own values (the enrichment is authoritative when present),
                    // cached by correlation id (#213).
                    const enr = enrichment[c.correlationId];
                    // Coerce each Genesys-native field to a plain string before
                    // render (#556): the enrichment is an unvalidated cast and an
                    // AI summary can arrive as a structured object, which would
                    // otherwise crash the page as an invalid React child.
                    const wrapCode =
                      displayText(enr?.wrapCode) ?? displayText(c.wrapCode);
                    const queue =
                      displayText(enr?.queue) ?? displayText(c.queue);
                    const callSummary =
                      displayText(enr?.callSummary) ??
                      displayText(c.callSummary);
                    return (
                      <tr
                        key={c.correlationId}
                        onClick={() => selectCall(c)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            selectCall(c);
                          }
                        }}
                        tabIndex={0}
                        aria-selected={isSelected}
                        aria-label={`Select call from ${c.agentUpn ?? 'agent'} on ${formatDateTime(c.startedAt)}`}
                        className={`cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#005fcc] ${
                          isSelected ? 'bg-[#F8EEE3]' : 'hover:bg-[#f9fafb]'
                        }`}
                      >
                        <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3] break-all">
                          {/* For an Online call the GUID IS the Genesys
                              conversationId — link it to the Genesys interaction
                              page (recording/transcript) in a new tab (#252).
                              Offline calls carry a Web-App GUID, so stay plain
                              text. stopPropagation keeps the link click from
                              also toggling the row selection. */}
                          {isOnline && c.correlationId ? (
                            <a
                              href={genesysInteractionUrl(c.correlationId)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Open Genesys interaction ${c.correlationId} in a new tab`}
                              className="text-[#D23A06] underline decoration-[#D23A06] hover:no-underline focus:outline-none focus:ring-2 focus:ring-[#005fcc] rounded"
                            >
                              {c.correlationId}
                            </a>
                          ) : (
                            correlationFor(c)
                          )}
                        </td>
                        <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                          {c.agentUpn ?? '—'}
                        </td>
                        <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                          {formatDateTime(c.startedAt)}
                        </td>
                        <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                          {callTypeLabel(c)}
                        </td>
                        <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                          {/* Wrap code is Online-only (Genesys); blank otherwise. */}
                          {isOnline ? (wrapCode ?? '—') : '—'}
                        </td>
                        <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                          {/* Queue is Online-only (Genesys); blank otherwise. */}
                          {isOnline ? (queue ?? '—') : '—'}
                        </td>
                        <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-[#dad7d3]">
                          {/* Call notes (AI summary) are Online-only and are only
                              retrieved when the agent opens the call (selects the
                              row) — CCF-104. */}
                          {isOnline
                            ? isSelected
                              ? (callSummary ?? '—')
                              : 'Select to view'
                            : '—'}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-3 text-[14px] font-['Lato','Arial',sans-serif] text-[#6b7280]"
                    >
                      No call history for this customer.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pager (#165): the table shows 10 interactions per page, newest
              first; Next loads the following 10 and Previous goes back. The
              buttons disable at the bounds. */}
          {groupedCalls.length > 0 && (
            <div className="flex items-center gap-4 mt-4 max-w-[1400px]">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                aria-label="Previous page of call history"
                className="inline-flex items-center justify-center px-5 py-2 min-h-[44px] bg-[#FF703E] text-[#1D252C] font-['Lato','Arial',sans-serif] text-[14px] font-bold rounded-[4px] shadow-sm hover:bg-[#e55a2a] hover:shadow-md focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#FF703E] disabled:hover:shadow-sm"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={currentPage >= pageCount - 1}
                aria-label="Next page of call history"
                className="inline-flex items-center justify-center px-5 py-2 min-h-[44px] bg-[#FF703E] text-[#1D252C] font-['Lato','Arial',sans-serif] text-[14px] font-bold rounded-[4px] shadow-sm hover:bg-[#e55a2a] hover:shadow-md focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#FF703E] disabled:hover:shadow-sm"
              >
                Next
              </button>
              <span
                className="text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c]"
                aria-live="polite"
                data-testid="call-history-pager-status"
              >
                Showing {showingFrom}–{showingTo} of {groupedCalls.length}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Call details — the selected call's events (CCF-112 "Event Details"):
          the named business events created automatically when an agent triggers
          an in-scope action during that call, each with its outcome, time and
          the agent who triggered it. Appears only once a call is selected. */}
      {loaded && selectedCall && (
        <fieldset className="mb-6 mt-10 max-w-[1400px]">
          <legend className="text-[18px] font-['Lato','Arial',sans-serif] font-bold text-[#1d252c] mb-2">
            Call details
          </legend>
          <div className="border-2 border-[#dad7d3] bg-white overflow-x-auto">
            <table
              className="w-full border-collapse"
              role="table"
              aria-label="Call details"
            >
              <thead>
                <tr className="bg-[#f3f4f6]">
                  {['Event', 'Event outcome', 'Date/time', 'Agent'].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      className="text-left px-4 py-3 text-[14px] font-['Lato','Arial',sans-serif] font-bold text-[#1d252c] border-b-2 border-r border-[#dad7d3] last:border-r-0"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selectedEvents.length > 0 ? (
                  selectedEvents.map((e, i) => (
                    <tr
                      key={`${e.interactionId}-${e.occurredAt}-${i}`}
                      className="hover:bg-[#f9fafb]"
                    >
                      <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                        {e.eventName}
                      </td>
                      <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                        {e.outcomeWording ?? e.outcome}
                      </td>
                      <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                        {formatDateTime(e.occurredAt)}
                      </td>
                      <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-[#dad7d3]">
                        {e.agentUpn ?? '—'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-3 text-[14px] font-['Lato','Arial',sans-serif] text-[#6b7280]"
                    >
                      No events recorded for this call.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </fieldset>
      )}
    </main>
  );
}
